import { useEffect, useMemo, useRef, useState } from "react"
import { Divider, LinearProgress } from "@mui/material"
import ResizableDrawer from "components/material/resizable-drawer"
import { useAnalytics } from "connected-components/analytics"
import { AI_DISCLAIMER } from "helpers/ai"
import PreferenceContext from "hooks/preferences"
import { useAccountId } from "resources/account"
import { AI_TEAMMATE_EVENTS, AiTeammateAnalyticsProvider } from "./analytics"
import ConfirmResetDialog from "./components/confirm-reset-dialog"
import AiTeammateHeader from "./components/header"
import AiTeammateMessageInput from "./components/message-input"
import AiTeammateMessagesPanel from "./components/messages-panel"
import { AiTeammateProvider } from "./context"
import { SendMessageProvider } from "./context/send-message"
import { useAiTeammateContext } from "./hooks/context"
import useFetchMessages from "./hooks/fetch-messages"
import "./style.scss"

export default function AiTeammateWrapper(props) {
  const accountId = useAccountId()

  const preferencesProps = useMemo(() => ({
    accountId,
    key: "ai-teammate",
    type: "ai-teammate",
  }), [accountId])

  return <PreferenceContext.Provider value={preferencesProps}>
    <AiTeammateProvider {...props}>
      <AiTeammateAnalyticsProvider>
        <SendMessageProvider>
          <AiTeammate {...props} />
        </SendMessageProvider>
      </AiTeammateAnalyticsProvider>
    </AiTeammateProvider>
  </PreferenceContext.Provider>
}

function AiTeammate() {
  const {
    isOpen,
    onClose,
    drawerWidth,
    isPreferencesLoaded,
    setDrawerWidth,
  } = useAiTeammateContext()
  const analytics = useAnalytics()
  const [isResetConfirmDialogOpen, setIsResetConfirmDialogOpen] = useState(false)
  const { isFetchingMessages } = useFetchMessages()

  // Using a ref to track the previous open state to track opening and closing events properly
  const isOpenRef = useRef(isOpen)

  useEffect(() => {
    if (isOpen && !isOpenRef.current) {
      analytics.track(AI_TEAMMATE_EVENTS.DRAWER_OPENED)
    } else if (!isOpen && isOpenRef.current) {
      analytics.track(AI_TEAMMATE_EVENTS.DRAWER_CLOSED)
    }
    isOpenRef.current = isOpen
  }, [isOpen, analytics])

  const container = useRef(null)

  function handleReset() {
    setIsResetConfirmDialogOpen(true)
  }

  function handleCloseResetConfirmDialog() {
    setIsResetConfirmDialogOpen(false)
  }

  // Wait until the preferences are loaded to avoid flickering
  if (!isPreferencesLoaded) {
    return null
  }

  const progressBar = isFetchingMessages ? <LinearProgress sx={{ minHeight: 3 }} /> : <div style={{ minHeight: 3 }} />

  return <span className="connected-components_ai-teammate" ref={container}>
    <ResizableDrawer initialWidth={drawerWidth || "25%"} open={isOpen} onClose={onClose} onResize={setDrawerWidth} closeOnBackdropClick>
      {/* Only render the contents when the drawer is open, but still render the drawer container so the animation works */}
      {isOpen && <div className="ai-teammate-container">
        <AiTeammateHeader onReset={handleReset} onClose={onClose} />
        <AiTeammateMessagesPanel />
        {progressBar}
        <Divider />
        <AiTeammateMessageInput />
        <div className="ai-teammate-footer">
          {AI_DISCLAIMER}
        </div>
      </div>}
    </ResizableDrawer>
    <ConfirmResetDialog isOpen={isResetConfirmDialogOpen} onClose={handleCloseResetConfirmDialog} parentContainer={container} />
  </span>
}
